import React, { useCallback, useEffect, useState } from "react";
import FinalDemoCard from "./FinalDemoCard";
import BackButton from "../../../../../components/BackButton/BackButton";
import { useSearchParams } from "react-router-dom";
import { useGetAllSchools } from "../../../Dashboard";
import SearchFilterBar from "../components/SearchFilterBar";
import { LoadingSpinner, Pagination } from "./SchoolList";

type FilterOption = {
  value: string;
  name: string;
};

type FiltersState = {
  trustee: FilterOption | null;
  institute: FilterOption | null;
};

function FinalDemoLeft() {
  const {
    data: schoolsData,
    loading: schoolsLoading,
    error: schoolsError,
    refetch: refetchSchoolsQuery,
  } = useGetAllSchools();

  const [searchParams, setSearchParams] = useSearchParams();

  const initialPage = parseInt(searchParams.get("page") || "1");
  const initialItemsPerRow = parseInt(searchParams.get("limit") || "10");
  const initialSearchText = searchParams.get("search") || "";
  const initialSearchFilter = searchParams.get("filter") || "school_id";
  const initialTrusteeId = searchParams.get("trustee_id") || "";
  const initialSchoolId = searchParams.get("school_id") || "";
  const initialTrusteeName = searchParams.get("trustee_name") || "";
  const initialSchoolName = searchParams.get("school_name") || "";

  const [searchText, setSearchText] = useState(initialSearchText);
  const [searchFilter, setSearchFilter] = useState(initialSearchFilter);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [itemsPerRow, setItemsPerRow] = useState(initialItemsPerRow);
  const [refetchLoading, setRefetchLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [localSchoolData, setLocalSchoolData] = useState<any>(null);

  const [filters, setFilters] = useState<FiltersState>({
    trustee: initialTrusteeId
      ? { value: initialTrusteeId, name: initialTrusteeName }
      : null,
    institute: initialSchoolId
      ? { value: initialSchoolId, name: initialSchoolName }
      : null,
  });

  const buildRefetchParams = useCallback(
    (page: number) => {
      const params: any = {
        page,
        limit: itemsPerRow,
      };

      if (searchText.trim()) {
        if (searchFilter === "school_id") params.school_id = searchText.trim();
        if (searchFilter === "school_name")
          params.school_name = searchText.trim();
        if (searchFilter === "trustee_id")
          params.trustee_id = searchText.trim();
      }

      if (filters?.trustee && searchFilter !== "trustee_id") {
        params.trustee_id = filters.trustee.value;
      }

      if (filters?.institute && searchFilter !== "school_id") {
        params.school_id = filters.institute.value;
      }

      return params;
    },
    [filters, itemsPerRow, searchFilter, searchText]
  );

  useEffect(() => {
    if (schoolsData?.getallschool) {
      setLocalSchoolData(schoolsData.getallschool);
    }
  }, [schoolsData]);

  useEffect(() => {
    const params: any = {
      page: currentPage.toString(),
      limit: itemsPerRow.toString(),
    };

    if (searchText) {
      params.search = searchText;
      params.filter = searchFilter;
    }

    if (filters.trustee) {
      params.trustee_id = filters.trustee.value;
      params.trustee_name = filters.trustee.name;
    }

    if (filters.institute) {
      params.school_id = filters.institute.value;
      params.school_name = filters.institute.name;
    }

    setSearchParams(params);
  }, [
    currentPage,
    itemsPerRow,
    searchText,
    filters,
    searchFilter,
    setSearchParams,
  ]);

  const refetchSchools = async (params?: any) => {
    try {
      setRefetchLoading(true);
      const result = await refetchSchoolsQuery(params || {});
      if (result.data?.getAllSchools) {
        setLocalSchoolData(result.data.getAllSchools);
      }
      return result;
    } finally {
      setRefetchLoading(false);
    }
  };

  const handlePageChange = async (page: number) => {
    setCurrentPage(page);
    setLoading(true);
    try {
      const params = buildRefetchParams(page);
      await refetchSchools(params);
    } finally {
      setLoading(false);
    }
  };

  const isLoading =
    schoolsLoading || loading || refetchLoading || !localSchoolData;

  const hasNoData =
    !localSchoolData ||
    !localSchoolData.data ||
    localSchoolData.data.length === 0;

  if (schoolsError) {
    return (
      <div className="text-center py-8 text-red-500">
        Error loading school details
      </div>
    );
  }

  return (
    <div>
      <BackButton to="/school" label="Final Demo Left" />

      <div className="p-4">
        <SearchFilterBar
          refetchSchools={refetchSchools}
          itemsPerRow={itemsPerRow}
          setItemsPerRow={setItemsPerRow}
          setCurrentPage={setCurrentPage}
          searchText={searchText}
          setSearchText={setSearchText}
          searchFilter={searchFilter}
          setSearchFilter={setSearchFilter}
          filters={filters}
          setFilters={setFilters}
          setLoading={setLoading}
          refetchLoading={refetchLoading}
          setRefetchLoading={setRefetchLoading}
        />

        {isLoading ? (
          <LoadingSpinner />
        ) : hasNoData ? (
          <div className="flex flex-col items-center justify-center min-h-[400px] bg-white rounded-lg border border-gray-200 p-8">
            <div className="text-xl text-gray-500 mb-4">No schools found</div>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {localSchoolData.data.map((school: any, index: number) => (
                <FinalDemoCard key={school._id || index} school={school} />
              ))}
            </div>

            {(() => {
              const totalItems = localSchoolData.pagination?.total;
              const totalPages = Math.ceil(totalItems / itemsPerRow);

              return totalPages > 1 ? (
                <div className="mt-6 flex justify-center items-center">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                  />
                </div>
              ) : null;
            })()}
          </>
        )}
      </div>
    </div>
  );
}

export default FinalDemoLeft;
